import * as React from 'react'
// Named import on purpose: the default export crashes the production build.
import { Icon as MdiIcon } from '@mdi/react'
import { cn } from '../../lib/utils'

/**
 * `IconTile` — the gradient tile that marks *what a thing is*: a page, a KPI
 * family, an asset type, the product itself (spec 2026-09-17-02).
 *
 * The rule it exists to enforce: **colour by meaning, gradients on identity**.
 * Data, controls and text stay flat. At most one `lg` tile per view; `sm`
 * tiles may repeat in rows.
 */

export const ICON_TILE_GRADIENTS = [
  'brand',
  'economic',
  'incidents',
  'compliance',
  'asset-site',
  'asset-building',
  'asset-unit',
  'asset-equipment',
  'asset-space',
  'neutral',
] as const

export type IconTileGradient = (typeof ICON_TILE_GRADIENTS)[number]

/**
 * Tailwind cannot see a class name built at runtime, so the mapping is a
 * literal table. Each utility is declared in `index.css` (`@utility
 * bg-gradient-*`) because Tailwind does not namespace `background-image`.
 */
export const ICON_TILE_GRADIENT_CLASSES: Record<IconTileGradient, string> = {
  brand: 'bg-gradient-brand',
  economic: 'bg-gradient-economic',
  incidents: 'bg-gradient-incidents',
  compliance: 'bg-gradient-compliance',
  'asset-site': 'bg-gradient-asset-site',
  'asset-building': 'bg-gradient-asset-building',
  'asset-unit': 'bg-gradient-asset-unit',
  'asset-equipment': 'bg-gradient-asset-equipment',
  'asset-space': 'bg-gradient-asset-space',
  neutral: 'bg-gradient-neutral',
}

export type IconTileSize = 'sm' | 'md' | 'lg' | 'xl'

/** side / corner radius / glyph, all in px. */
export const ICON_TILE_SIZES: Record<
  IconTileSize,
  { box: number; radius: number; glyph: number }
> = {
  sm: { box: 24, radius: 7, glyph: 14 },
  md: { box: 32, radius: 8, glyph: 18 },
  lg: { box: 40, radius: 11, glyph: 24 },
  xl: { box: 56, radius: 14, glyph: 32 },
}

/** Font size of an `initial` tile's letter, in px, one per box size. */
export const ICON_TILE_INITIAL_FONT_SIZE: Record<IconTileSize, number> = {
  sm: 11,
  md: 13,
  lg: 16,
  xl: 22,
}

export type IconTileTone = 'solid' | 'soft'

/** The glyph holds on the lighter stop of the gradient. */
export const ICON_TILE_GLYPH_SHADOW = 'drop-shadow(0 1px 0 oklch(0 0 0 / 12%))'

export interface IconTileProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'> {
  /** MDI path. Omit it when the tile carries an `initial` instead. */
  path?: string
  /**
   * A letter to draw in place of a glyph — the organisation's initial in the
   * sidebar brand, where no icon says anything the name does not. Ignored
   * when `path` is set.
   */
  initial?: string
  gradient?: IconTileGradient
  size?: IconTileSize
  /**
   * `solid` fills with the gradient and paints the glyph white. `soft` tints
   * with 10 % of the gradient's first stop and paints the glyph in that
   * colour — for dense contexts where a saturated tile on every row shouts.
   */
  tone?: IconTileTone
  /** Rendered as an `sr-only` span; without it the tile is `aria-hidden`. */
  label?: string
}

function IconTile({
  path,
  initial,
  gradient = 'neutral',
  size = 'md',
  tone = 'solid',
  label,
  className,
  style,
  ...props
}: IconTileProps) {
  const { box, radius, glyph } = ICON_TILE_SIZES[size]
  const solid = tone === 'solid'
  const seed = `var(--gradient-${gradient}-from)`

  return (
    <span
      aria-hidden={label ? undefined : true}
      className={cn(
        'inline-flex shrink-0 items-center justify-center',
        solid
          ? cn(ICON_TILE_GRADIENT_CLASSES[gradient], 'text-white shadow-tile')
          : null,
        className
      )}
      style={{
        width: box,
        height: box,
        borderRadius: radius,
        ...(solid
          ? null
          : {
              backgroundColor: `color-mix(in oklch, ${seed} 10%, transparent)`,
              color: seed,
            }),
        ...style,
      }}
      {...props}
    >
      {path ? (
        <MdiIcon
          path={path}
          size={`${glyph}px`}
          // `text-current` keeps the tile's own glyph colour: hosts such as
          // CommandItem paint every class-less descendant svg muted (#1678).
          className="text-current"
          style={solid ? { filter: ICON_TILE_GLYPH_SHADOW } : undefined}
        />
      ) : (
        <span
          className="font-semibold leading-none"
          style={{
            fontSize: ICON_TILE_INITIAL_FONT_SIZE[size],
            ...(solid ? { filter: ICON_TILE_GLYPH_SHADOW } : null),
          }}
        >
          {(initial ?? '').slice(0, 1).toUpperCase()}
        </span>
      )}
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  )
}

export { IconTile }
